import { VehicleRowVM } from './vehicleRowVM';

/**
 * 回転の追加・削除は勤務 (delivery) ごとに扱う。
 *
 * truckId で束ねていたため、1 台に 2 つの勤務が乗るとき別の勤務に回転が足されていた
 * （報告: 矢野 利秀の「回転追加」がドライバー未設定に入った）。回転は勤務の中の概念。
 */

// 追加回転の次番号 = サーバ最大とローカル最大の +1（Presenter.tsx:1773-1779）。
export function nextRotationNumber(
  sourceRowsRaw: VehicleRowVM[],
  existing: number[],
  deliveryId: number,
): number {
  const serverMax = Math.max(
    0,
    ...sourceRowsRaw.filter((r) => r.deliveryId === deliveryId).map((r) => r.rotationNumber),
  );
  const localMax = Math.max(0, ...existing);
  return Math.max(serverMax, localMax) + 1;
}

// サーバで既に存在する回転を extraRotations から落とす（Presenter.tsx:624-644）。
// 変化が無ければ同一参照を返す（不要な再レンダー防止）。
export function dropServerExistingRotations(
  extraRotations: Map<number, number[]>,
  sourceRowsRaw: VehicleRowVM[],
): Map<number, number[]> {
  if (extraRotations.size === 0) return extraRotations;
  const serverRotationsByDelivery = new Map<number, Set<number>>();
  for (const r of sourceRowsRaw) {
    const set = serverRotationsByDelivery.get(r.deliveryId);
    if (set) set.add(r.rotationNumber);
    else serverRotationsByDelivery.set(r.deliveryId, new Set([r.rotationNumber]));
  }
  const next = new Map<number, number[]>();
  let changed = false;
  for (const [deliveryId, rns] of extraRotations) {
    const serverSet = serverRotationsByDelivery.get(deliveryId);
    const filtered = rns.filter((rn) => !(serverSet && serverSet.has(rn)));
    if (filtered.length !== rns.length) changed = true;
    if (filtered.length > 0) next.set(deliveryId, filtered);
  }
  return changed ? next : extraRotations;
}

// sourceRowsRaw に extraRotations をマージし空 placeholder 行を追加（Presenter.tsx:646-682）。
// placeholder は「再積み込み拠点」loading カード 1 枚のみ（unloading 無し＝地図線なし）。
export function mergeExtraRotations(
  sourceRowsRaw: VehicleRowVM[],
  extraRotations: Map<number, number[]>,
): VehicleRowVM[] {
  if (extraRotations.size === 0) return sourceRowsRaw;

  // 追加先の勤務から truck と乗務者を引く。truckId は placeholder カードの placeKey と
  // グループ解決 (adapter / vehicleRowVM) に、driverId は行の乗務者表示に要る
  // （引き継がないと「ドライバー未設定」と出て、別の人の勤務に足したように見える）
  const sourceByDelivery = new Map<number, VehicleRowVM>();
  for (const r of sourceRowsRaw) {
    if (!sourceByDelivery.has(r.deliveryId)) sourceByDelivery.set(r.deliveryId, r);
  }

  let placeholderSeed = -2000;
  const extras: VehicleRowVM[] = [];
  for (const [deliveryId, rns] of extraRotations) {
    const origin = sourceByDelivery.get(deliveryId);
    // 追加元の勤務が消えていれば足せない（削除された勤務に回転だけ残さない）
    if (!origin) continue;
    const truckId = origin.truckId;
    for (const rn of rns) {
      extras.push({
        deliveryId: placeholderSeed--,
        truckId,
        rotationNumber: rn,
        totalRotations: 0,
        driverId: origin.driverId,
        driverColor: '#9e9e9e',
        truckColor: null,
        totalItemCount: 0,
        cards: [{
          kind: 'loading',
          placeKey: `reload-${truckId}-${rn}`,
          placeName: '再積み込み拠点',
          timeLabel: null,
          itemCount: 0,
          operations: [],
          legRuns: [],
          isRouteDeviation: false,
          isAutoAssigned: false,
        }],
        isPlaceholderRow: true,
        isExtraRotation: true,
        // どの勤務に足した回転かを覚えておく。deliveryId は負のダミーなので、
        // これが無いと追加先を後から引けない
        extraOfDeliveryId: deliveryId,
      });
    }
  }

  const merged: VehicleRowVM[] = [...sourceRowsRaw, ...extras];
  // 総回転数も勤務ごと。truck 全体で数えると、別の勤務に足した回転で
  // 1 人目の「◯/N 回転目」まで増える
  const rotCount = new Map<number, number>();
  const groupKeyOf = (r: VehicleRowVM) => r.extraOfDeliveryId ?? r.deliveryId;
  merged.forEach((r) => rotCount.set(groupKeyOf(r), (rotCount.get(groupKeyOf(r)) ?? 0) + 1));
  return merged.map((r) => ({ ...r, totalRotations: rotCount.get(groupKeyOf(r)) ?? 1 }));
}
